import * as Sentry from '@sentry/react';
import { push } from 'connected-react-router';
import qs from 'query-string';

import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { notifyTransferUrl, onboardingValidateUrl } from 'src/app/client/api';
import { fetchActivePending, accountOnboardingValidate, notifyTransfer } from 'src/app/client/models/account';
import { STEP_NAME } from 'src/app/components/Pages/GetStarted/StepLayout/consts';
import { getStarted } from 'src/app/router/paths';
import { segmentTrack } from 'src/app/state/actions';
import * as actionTypes from 'src/app/state/actions/types';
import { getCurrentPathParams } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { OptimisticPendingTransfer } from 'src/app/types';
import { extractErrorCodes, parseMarketingParams } from 'src/app/utils';
import { flattenPrescriptionItems } from 'src/app/utils/medicationUtils';

export * from 'src/app/state/actions/types';

export type OnboardingTransfer = {
  medicationName: string;
  hiclSeqno?: string | null;
  doctorRequiredForTransfer?: boolean;
  eligibleForTransfer?: boolean;
  doctorName?: string;
  doctorPhone?: string;
  doctorNpi?: string;
};

export type CreateAccountWithTransfer = {
  firstName: string;
  lastName: string;
  sex: string;
  birthdate: string;
  email: string;
  password: string;
  phone: string;
  medications: OnboardingTransfer[];
  pharmacyName: string;
  pharmacyDetails: string;
  pharmacyPhone: string;
  zipCode: string;
  referral: string;
  signupSource?: string;
  employeeBadge?: string;
  accountId: string;
  allergies?: string[];
};

export type CreateAccountWithDoctorTransfer = CreateAccountWithTransfer & {
  doctorName?: string;
  doctorPhone?: string;
  doctorNpi?: string;
  referral?: string;
};

export type CreateAccountWithDoctorTransferAndAllMeds = CreateAccountWithDoctorTransfer & {
  shouldTransferAllMeds: boolean;
};

export type UserValues = {
  email: string;
  password: string;
};

export function onboardingAccountValidate(data) {
  return async (_dispatch, getState) => {
    const state = getState();
    const { client } = state;
    try {
      const response = await accountOnboardingValidate(client, data);
      return response;
    } catch (error) {
      const bffError = error?.error ?? '';
      const api = onboardingValidateUrl();
      const trackErrorPayload = {
        errorMessages: 'Onboarding Validate Account Failed',
        errorSummary: `Title: ${bffError?.title ?? ''} | Flavor Text: ${bffError?.flavorText ?? ''}`,
        api,
        errorCode: extractErrorCodes(error),
        source: window.location.href,
      };

      segmentTrack(UNIFIED.error, trackErrorPayload);
      throw error;
    }
  };
}

/**
 *
 * @param rxTransfers OnboardingTransfer[]
 * @param doctorTransfer boolean
 * @returns OptimisticPendingTransfer[]
 */
export const transformOnboardingTransfers = (
  rxTransfers: OnboardingTransfer[] = [],
  doctorTransfer = false
): OptimisticPendingTransfer[] => {
  return rxTransfers.map(transfer => ({
    name: transfer.medicationName,
    source: doctorTransfer ? 'doctor' : 'pharmacy',
    attributes: {
      primaryName: transfer.medicationName,
      eligibleForTransfer: transfer.eligibleForTransfer,
      doctorRequiredForTransfer: transfer.doctorRequiredForTransfer,
    },
  }));
};

export interface AccountOnboardingCreateProps {
  onBoardingAccount: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    password: string;
    referral: string;
    self_attribution: string;
    signup_source: string;
    agreed_to_terms_version: number;
  };
  onBoardingPrescriptions?: {
    medications: OnboardingTransfer[];
    pharmacyDetails: string;
    pharmacyName: string;
  };
  onBoardingPatient?: {
    sex: string;
    birthdate: string;
    allergies?: string;
    medical_conditions?: string;
    other_medications?: string;
  };
}

export function onboardingAccountNotifyTransfer(accountId) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    // Do not throw on notify transfer errors
    try {
      const response = await notifyTransfer(client, { accountId });
      return response;
    } catch (e) {
      const bffError = e?.error ?? '';
      const api = notifyTransferUrl(accountId);
      const trackErrorPayload = {
        errorMessages: 'Onboarding Notify Transfer Failed',
        errorSummary: `Title: ${bffError?.title ?? ''} | Flavor Text: ${bffError?.flavorText ?? ''}`,
        api,
        errorCode: extractErrorCodes(e),
        source: window.location.href,
      };
      segmentTrack(UNIFIED.error, trackErrorPayload);
      console.error(e);
      Sentry.captureException(e);
    }
  };
}

export const getPatientMedications = async (client, accountId) => {
  const response = await fetchActivePending(client, { accountId });
  const medications = flattenPrescriptionItems(response?.data?.active);
  const medNames: OptimisticPendingTransfer[] = medications.map(med => ({ name: med.name, source: med.source }));
  return medNames;
};

export const setOnboardingEscripts = escriptCounts => ({
  type: actionTypes.SET_ONBOARDING_ESCRIPTS,
  escriptCounts,
});

export type redirectToOnboardingOptions = {
  isEligibilityConfirmation?: boolean;
  isDeliverable?: boolean;
  zipCode?: string;
  referral?: string;
};

export function redirectToOnboarding(source: string, options: redirectToOnboardingOptions = {}) {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const pathname = state.router?.location?.pathname;
    const redirectPath = `${getStarted.toPath()}`;
    const marketingParams = parseMarketingParams(getCurrentPathParams(state));
    const query = {
      ...marketingParams,
      zipCode: options.zipCode,
      isDeliverable: options.isDeliverable,
      cp_referral: options.referral,
    };

    if (options.isDeliverable) {
      query.step = STEP_NAME.ELIGIBILITY_CONFIRMATION.toLowerCase();
    } else if (options.isDeliverable === false) {
      query.step = STEP_NAME.INELIGIBLE_STEP.toLowerCase();
    }

    dispatch(segmentTrack(UNIFIED.beginOnboarding, { pagepath: pathname, source }));
    dispatch(
      push({
        pathname: `${redirectPath}`,
        search: `?${qs.stringify(query)}`,
      })
    );
  };
}

/**
 *  Sets medications successfully found via escript match or submitted via onboarding for welcome screens
 * @param medications
 * @returns
 */
export function setMedicationSuccess(medications: OptimisticPendingTransfer[]) {
  return {
    type: actionTypes.SET_MEDICATION_SUCCESS,
    medications: medications,
  };
}

/**
 *  Sets optimistic pending transfers from onboarding
 * @param transfers OptimisticPendingTransfer[]
 * @returns
 */
export function setOnboardingTransfersSuccess(transfers: OptimisticPendingTransfer[]) {
  return {
    type: actionTypes.SET_OPTIMISTIC_PENDING_TRANSFER,
    optimisticPendingTransfers: transfers,
  };
}
